// src/components/PuzzlePiece.tsx
"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import {
  ALIGN_DURATION,
  EMBED_DURATION,
  MAX_GLOW_ANGLE,
  SOLVE_ANGLE_EPS,
  SOLVE_DWELL_TIME,
  PIECE_START_POS,
  TARGET_ALIGN_POS,
  TARGET_SOLVE_ROT,
  FINAL_EMBED_POS,
  shortestAngleDiff,
  SCENE_FADE_IN_DURATION,
  GLOW_COLOR,
  GLOW_BASE_INTENSITY,
  GLOW_MAX_INTENSITY,
  GLOW_LERP_SPEED,
  POINT_LIGHT_DISTANCE,
  POINT_LIGHT_DECAY,
  POINT_LIGHT_INTENSITY_MULT,
  POINT_LIGHT_OFFSET,
  POINT_LIGHT_OFFSET_SOLVED,
  POINT_LIGHT_OFFSET_LERP_SPEED,
  FRONT_SPOT_DISTANCE,
  FRONT_SPOT_INTENSITY,
  FRONT_SPOT_ANGLE,
  FRONT_SPOT_PENUMBRA,
  FRONT_SPOT_DECAY,
  FRONT_SPOT_FADE_SPEED,
  POST_SOLVE_FLICKER_CONFIG,
  EDGE_STRENGTH,
  EDGE_BIAS,
  PIECE_INITIAL_ROT_X_DEG,
  PIECE_INITIAL_ROT_Y_DEG,
  ROT_SPEED,
} from "./puzzleConfig";

type Phase = "idle" | "aligning" | "embedding" | "done";

export type PuzzlePieceProps = {
  onProximityChange?: (value01: number) => void; // 0..1 (0 = far, 1 = perfect)
  onSolved?: () => void;                          // called once embedding finishes
  onSolveBegin?: () => void;                      // called when aligning starts
};

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

export function PuzzlePiece({
  onProximityChange,
  onSolved,
  onSolveBegin,
}: PuzzlePieceProps) {
  const groupRef = useRef<THREE.Group>(null);
  const pointLightRef = useRef<THREE.PointLight | null>(null);
  const frontSpotRef = useRef<THREE.SpotLight | null>(null);

  const { gl } = useThree();
  const { scene } = useGLTF("/models/puzzle.glb") as any;

  const rotation = useRef<[number, number]>([
    THREE.MathUtils.degToRad(PIECE_INITIAL_ROT_X_DEG),
    THREE.MathUtils.degToRad(PIECE_INITIAL_ROT_Y_DEG),
  ]);

  const [phase, setPhase] = useState<Phase>("idle");
  // Mirror phase in a ref so canvas-level event handlers always see the current value
  const phaseRef = useRef<Phase>("idle");
  // Drag state lives in a ref (no re-render needed; canvas events are native)
  const isDraggingRef = useRef(false);

  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const alignProgress = useRef(0);
  const embedProgress = useRef(0);
  const solveDwellTimer = useRef(0);

  const alignStartRot = useRef(new THREE.Euler());
  const alignEndRot   = useRef(new THREE.Euler()); // shortest-path target from alignStartRot
  const alignStartPos = useRef(new THREE.Vector3());

  const lastProximity = useRef(0);

  // Emissive control
  const emissiveMaterials = useRef<THREE.MeshStandardMaterial[]>([]);
  const glowLevel = useRef(0); // 0..1

  // Point light offset lerp (pre-solve → post-solve position)
  const pointLightOffset = useRef(POINT_LIGHT_OFFSET.clone());

  // Front spot visibility factor (0..1)
  const frontSpotVisibility = useRef(0);

  // Scene fade-in (prevents green flash on load)
  const sceneFadeIn = useRef(0);

  // Post-solve infinite flicker state
  const postSolveActive = useRef(false);
  const postSolvePhase = useRef<"idle" | "burst">("idle");
  const postSolveTime = useRef(0);
  const postSolveDuration = useRef(0);
  const postSolveMinFactor = useRef(1);
  const postSolveMaxFactor = useRef(1);
  const postSolveFreq = useRef(0);

  const setupIdleSegment = () => {
    postSolvePhase.current = "idle";
    postSolveDuration.current = rand(
      POST_SOLVE_FLICKER_CONFIG.idleDurationMin,
      POST_SOLVE_FLICKER_CONFIG.idleDurationMax
    );
    postSolveTime.current = 0;
    postSolveMinFactor.current = 1;
    postSolveMaxFactor.current = 1;
    postSolveFreq.current = 0;
  };

  const setupBurstSegment = () => {
    const [minMin, minMax] = POST_SOLVE_FLICKER_CONFIG.intensityMinRange;
    const [maxMin, maxMax] = POST_SOLVE_FLICKER_CONFIG.intensityMaxRange;

    postSolvePhase.current = "burst";
    postSolveDuration.current = rand(
      POST_SOLVE_FLICKER_CONFIG.burstDurationMin,
      POST_SOLVE_FLICKER_CONFIG.burstDurationMax
    );
    postSolveTime.current = 0;
    postSolveMinFactor.current = rand(minMin, minMax);
    postSolveMaxFactor.current = rand(maxMin, maxMax);
    postSolveFreq.current = rand(
      POST_SOLVE_FLICKER_CONFIG.freqMin,
      POST_SOLVE_FLICKER_CONFIG.freqMax
    );
  };

  // Clone piece and set up emissive + smooth edge darkening
  const pieceScene = useMemo(() => {
    emissiveMaterials.current = [];

    const clone = scene.clone(true);

    clone.traverse((obj: any) => {
      if (!obj.isMesh) return;

      const mesh = obj as THREE.Mesh;
      mesh.castShadow = true;

      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;

      const processMaterial = (material: THREE.Material) => {
        const std = material as THREE.MeshStandardMaterial & {
          emissiveIntensity?: number;
          userData?: any;
        };

        if ("emissive" in std) {
          std.emissive = GLOW_COLOR.clone();
          std.emissiveIntensity = GLOW_BASE_INTENSITY;
          emissiveMaterials.current.push(std);
        }

        const uData = std.userData || (std.userData = {});
        if (uData.__edgeDarkeningPatched) return;
        uData.__edgeDarkeningPatched = true;

        std.onBeforeCompile = (shader: any) => {
          shader.uniforms.uEdgeStrength = { value: EDGE_STRENGTH };
          shader.uniforms.uEdgeBias = { value: EDGE_BIAS };

          // Vertex: compute view-angle factor
          shader.vertexShader =
            `
varying float vViewFactor;
` + shader.vertexShader;

          shader.vertexShader = shader.vertexShader.replace(
            "#include <project_vertex>",
            `
#include <project_vertex>
{
  vec3 viewDir = normalize( -mvPosition.xyz );
  vViewFactor = abs( dot( normalize( transformedNormal ), viewDir ) );
}
`
          );

          // Fragment: darken edges according to vViewFactor
          shader.fragmentShader =
            `
varying float vViewFactor;
uniform float uEdgeStrength;
uniform float uEdgeBias;
` + shader.fragmentShader;

          shader.fragmentShader = shader.fragmentShader.replace(
            "#include <output_fragment>",
            `
#include <output_fragment>

// vViewFactor ~1 at center, ~0 at silhouette
float edge = clamp(1.0 - vViewFactor + uEdgeBias, 0.0, 1.0);
edge = edge * edge; // soften
float borderFactor = 1.0 - edge * uEdgeStrength;

// Darken final color (lights + emissive) near silhouette
gl_FragColor.rgb *= borderFactor;
`
          );
        };
      };

      if (Array.isArray(mat)) {
        mat.forEach(processMaterial);
      } else if (mat) {
        processMaterial(mat);
      }
    });

    return clone;
  }, [scene]);

  const reportProximity = useCallback(
    (p: number) => {
      const clamped = THREE.MathUtils.clamp(p, 0, 1);
      const changed = Math.abs(clamped - lastProximity.current) > 0.005;
      lastProximity.current = clamped;
      if (changed) {
        onProximityChange?.(clamped);
      }
    },
    [onProximityChange]
  );

  // Attach drag events to the whole canvas so any click-drag anywhere rotates the piece
  useEffect(() => {
    const canvas = gl.domElement;

    const onDown = (e: PointerEvent) => {
      if (phaseRef.current !== "idle") return;
      isDraggingRef.current = true;
      lastPointer.current = { x: e.clientX, y: e.clientY };
      canvas.setPointerCapture(e.pointerId);
      canvas.style.cursor = "grabbing";
    };

    const onMove = (e: PointerEvent) => {
      if (!isDraggingRef.current || phaseRef.current !== "idle") return;
      const lp = lastPointer.current;
      if (!lp) return;
      const dx = e.clientX - lp.x;
      const dy = e.clientY - lp.y;
      lastPointer.current = { x: e.clientX, y: e.clientY };
      rotation.current[1] += dx * ROT_SPEED;
      rotation.current[0] += dy * ROT_SPEED;
    };

    const onUp = (e: PointerEvent) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      lastPointer.current = null;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      canvas.style.cursor = "";
    };

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);

    return () => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
    };
  }, [gl.domElement]);

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;

    // ─────────────────────────────────────────────
    // Phase: motion / rotation
    // ─────────────────────────────────────────────

    if (phase === "idle") {
      group.rotation.x = rotation.current[0];
      group.rotation.y = rotation.current[1];

      group.position.lerp(PIECE_START_POS, 0.1);

      const dx = shortestAngleDiff(group.rotation.x, TARGET_SOLVE_ROT.x);
      const dy = shortestAngleDiff(group.rotation.y, TARGET_SOLVE_ROT.y);
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      const angleError = Math.max(adx, ady);

      const proximity =
        1 - THREE.MathUtils.clamp(angleError / MAX_GLOW_ANGLE, 0, 1);
      reportProximity(proximity);

      const insideSolveCone = adx < SOLVE_ANGLE_EPS && ady < SOLVE_ANGLE_EPS;

      if (insideSolveCone) {
        solveDwellTimer.current += delta;
        if (solveDwellTimer.current >= SOLVE_DWELL_TIME) {
          reportProximity(0);
          phaseRef.current = "aligning";
          setPhase("aligning");
          alignProgress.current = 0;
          alignStartRot.current.copy(group.rotation);
          alignStartPos.current.copy(group.position);
          // Compute shortest-path end rotation so the piece settles without spinning back
          alignEndRot.current.set(
            alignStartRot.current.x + shortestAngleDiff(TARGET_SOLVE_ROT.x, alignStartRot.current.x),
            alignStartRot.current.y + shortestAngleDiff(TARGET_SOLVE_ROT.y, alignStartRot.current.y),
            alignStartRot.current.z + shortestAngleDiff(TARGET_SOLVE_ROT.z, alignStartRot.current.z),
          );
          solveDwellTimer.current = 0;
          onSolveBegin?.();
        }
      } else {
        solveDwellTimer.current = 0;
      }
    } else if (phase === "aligning") {
      reportProximity(0);

      alignProgress.current += delta / ALIGN_DURATION;
      const t = THREE.MathUtils.clamp(alignProgress.current, 0, 1);
      const smoothT = t * t * (3 - 2 * t);

      group.rotation.x = THREE.MathUtils.lerp(alignStartRot.current.x, alignEndRot.current.x, smoothT);
      group.rotation.y = THREE.MathUtils.lerp(alignStartRot.current.y, alignEndRot.current.y, smoothT);
      group.rotation.z = THREE.MathUtils.lerp(alignStartRot.current.z, alignEndRot.current.z, smoothT);

      group.position.lerpVectors(
        alignStartPos.current,
        TARGET_ALIGN_POS,
        smoothT
      );

      rotation.current[0] = group.rotation.x;
      rotation.current[1] = group.rotation.y;

      if (t >= 1) {
        phaseRef.current = "embedding";
        setPhase("embedding");
        embedProgress.current = 0;
      }
    } else if (phase === "embedding") {
      reportProximity(0);

      embedProgress.current += delta / EMBED_DURATION;
      const t = THREE.MathUtils.clamp(embedProgress.current, 0, 1);
      const smoothT = t * t * (3 - 2 * t);

      group.rotation.copy(TARGET_SOLVE_ROT);
      group.position.lerpVectors(TARGET_ALIGN_POS, FINAL_EMBED_POS, smoothT);

      if (t >= 1) {
        phaseRef.current = "done";
        setPhase("done");
        onSolved?.();

        postSolveActive.current = true;
        setupBurstSegment();
      }
    } else if (phase === "done") {
      group.rotation.copy(TARGET_SOLVE_ROT);
      group.position.copy(FINAL_EMBED_POS);
    }

    // ─────────────────────────────────────────────
    // Glow intensity logic (pre-flicker)
    // ─────────────────────────────────────────────

    let targetGlowLevel = 0;

    if (phase === "idle") {
      targetGlowLevel = THREE.MathUtils.clamp(lastProximity.current, 0, 1);
    } else {
      targetGlowLevel = 1;
    }

    glowLevel.current = THREE.MathUtils.lerp(
      glowLevel.current,
      targetGlowLevel,
      1 - Math.exp(-GLOW_LERP_SPEED * delta)
    );

    const baseIntensity =
      GLOW_BASE_INTENSITY +
      glowLevel.current * (GLOW_MAX_INTENSITY - GLOW_BASE_INTENSITY);

    // ─────────────────────────────────────────────
    // Post-solve infinite flicker factor
    // ─────────────────────────────────────────────

    let flickerFactor = 1;

    if (postSolveActive.current && phase === "done") {
      postSolveTime.current += delta;

      if (postSolveTime.current >= postSolveDuration.current) {
        if (postSolvePhase.current === "burst") {
          setupIdleSegment();
        } else {
          setupBurstSegment();
        }
      }

      if (postSolvePhase.current === "burst" && postSolveFreq.current > 0) {
        const wave =
          0.5 +
          0.5 *
            Math.sin(
              2.0 * Math.PI * postSolveFreq.current * postSolveTime.current
            );
        flickerFactor = THREE.MathUtils.lerp(
          postSolveMinFactor.current,
          postSolveMaxFactor.current,
          wave
        );
      } else {
        flickerFactor = 1;
      }
    }

    // Scene fade-in (prevents green flash on load)
    sceneFadeIn.current = Math.min(sceneFadeIn.current + delta / SCENE_FADE_IN_DURATION, 1);
    const fade = sceneFadeIn.current;

    const finalIntensity = baseIntensity * flickerFactor * fade;

    // Apply to emissive
    emissiveMaterials.current.forEach((mat) => {
      mat.emissiveIntensity = finalIntensity;
      mat.emissive.copy(GLOW_COLOR);
    });

    // Apply to point light (same flicker curve + lerp offset pre→post solve)
    if (pointLightRef.current) {
      pointLightRef.current.intensity =
        finalIntensity * POINT_LIGHT_INTENSITY_MULT;

      const offsetTarget = phase === "done" ? POINT_LIGHT_OFFSET_SOLVED : POINT_LIGHT_OFFSET;
      pointLightOffset.current.lerp(offsetTarget, 1 - Math.exp(-POINT_LIGHT_OFFSET_LERP_SPEED * delta));
      pointLightRef.current.position.copy(pointLightOffset.current);
    }

    // ─────────────────────────────────────────────
    // Front spot: fade while unsolved → off when done
    // ─────────────────────────────────────────────

    const wantsFrontLight =
      phase === "idle" || phase === "aligning" || phase === "embedding";

    frontSpotVisibility.current = THREE.MathUtils.lerp(
      frontSpotVisibility.current,
      wantsFrontLight ? 1 : 0,
      1 - Math.exp(-FRONT_SPOT_FADE_SPEED * delta)
    );

    if (frontSpotRef.current) {
      const spot = frontSpotRef.current;
      spot.intensity = FRONT_SPOT_INTENSITY * frontSpotVisibility.current * fade;
      // The spot is positioned in front of the origin; it already points at (0,0,0)
      // via its default target, so no extra orientation is required here.
    }
  });

  return (
    <>
      {/* Viewer-facing front key light */}
      <spotLight
        ref={frontSpotRef}
        color={0xffffff}
        intensity={0} // starts at 0, faded in by useFrame
        distance={FRONT_SPOT_DISTANCE}
        angle={FRONT_SPOT_ANGLE}
        penumbra={FRONT_SPOT_PENUMBRA}
        decay={FRONT_SPOT_DECAY}
        position={[0, 0, FRONT_SPOT_DISTANCE]}
      />

      <group ref={groupRef} scale={1}>
        {/* Point light driven by the same intensity as emissive */}
        <pointLight
          ref={pointLightRef}
          color={GLOW_COLOR}
          intensity={0} // fully controlled in useFrame
          distance={POINT_LIGHT_DISTANCE}
          decay={POINT_LIGHT_DECAY}
          position={POINT_LIGHT_OFFSET.toArray()} // initial; lerped each frame in useFrame
        />

        <primitive object={pieceScene} />
      </group>
    </>
  );
}
